import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useRecordIncomingEmail } from '../hooks/reactQuerys/CommunicationQueries';
import { IIncomingEmailRequest } from '../Interfaces/ICommunication';

export interface RecordIncomingPrefill {
  from?: string;
  to?: string;
  cc?: string;
  subject?: string;
  body?: string;
  bodyContentType?: 'text' | 'html';
  receivedAt?: string; // ISO
  notes?: string;
  externalMessageId?: string;
  attachments?: AttachmentDraft[];
  /** Hinweis, der oben im Dialog angezeigt wird (z. B. „Aus .msg geladen") */
  infoMessage?: string;
  /** Default-Richtung. Wird beim Drop aus dem Mailprogramm gesetzt — der
   *  Anwender kann sie im Dialog noch aendern. */
  direction?: 'incoming' | 'outgoing';
}

interface IProps {
  idLegalClient?: number | null;
  idLegalTransaction?: number | null;
  onClose: () => void;
  onCreated: () => void;
  prefill?: RecordIncomingPrefill;
}

interface AttachmentDraft {
  fileName: string;
  data: string; // base64 (ohne data:-prefix)
}

const fileToBase64 = (file: File): Promise<AttachmentDraft> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve({
          fileName: file.name,
          data: result.split(',', 2)[1] ?? '',
        });
      } else {
        reject(new Error('Unsupported FileReader result'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Read error'));
    reader.readAsDataURL(file);
  });

// ISO → 'YYYY-MM-DDTHH:MM' (datetime-local Format, lokale Zeit)
const isoToLocalInput = (iso?: string): string => {
  if (!iso) {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export const RecordIncomingDialog: React.FC<IProps> = ({
  idLegalClient,
  idLegalTransaction,
  onClose,
  onCreated,
  prefill,
}) => {
  const [direction, setDirection] = useState<'incoming' | 'outgoing'>(
    prefill?.direction ?? 'incoming'
  );
  const [from, setFrom] = useState(prefill?.from ?? '');
  const [to, setTo] = useState(prefill?.to ?? '');
  const [cc, setCc] = useState(prefill?.cc ?? '');
  const [subject, setSubject] = useState(prefill?.subject ?? '');
  const [body, setBody] = useState(prefill?.body ?? '');
  const [bodyType, setBodyType] = useState<'text' | 'html'>(
    prefill?.bodyContentType ?? 'text'
  );
  const [receivedAt, setReceivedAt] = useState<string>(() =>
    isoToLocalInput(prefill?.receivedAt)
  );
  const [notes, setNotes] = useState(prefill?.notes ?? '');
  const [attachments, setAttachments] = useState<AttachmentDraft[]>(
    prefill?.attachments ?? []
  );
  const [error, setError] = useState<string | null>(null);

  const { recordIncoming, isRecordingIncoming } = useRecordIncomingEmail();

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const drafts: AttachmentDraft[] = [];
    for (const f of Array.from(files)) {
      drafts.push(await fileToBase64(f));
    }
    setAttachments(prev => [...prev, ...drafts]);
  };

  const submit = async () => {
    setError(null);
    if (!from.trim()) {
      setError('Absender (Von) ist erforderlich');
      return;
    }
    if (!subject.trim()) {
      setError('Betreff ist erforderlich');
      return;
    }
    if (!idLegalClient && !idLegalTransaction) {
      setError('Es muss mindestens ein Mandant oder Vorgang angegeben sein');
      return;
    }
    const payload: IIncomingEmailRequest = {
      idLegalClient: idLegalClient ?? undefined,
      idLegalTransaction: idLegalTransaction ?? undefined,
      Direction: direction,
      From: from.trim(),
      To: to.trim() || undefined,
      Cc: cc.trim() || undefined,
      Subject: subject.trim(),
      Body: body || undefined,
      BodyContentType: bodyType,
      ReceivedAt: receivedAt ? new Date(receivedAt).toISOString() : undefined,
      Notes: notes.trim() || undefined,
      ExternalMessageId: prefill?.externalMessageId,
      Attachments: attachments.map(a => ({
        FileName: a.fileName,
        Data: a.data,
      })),
    };
    try {
      await recordIncoming(payload);
      onCreated();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Konnte nicht gespeichert werden'
      );
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {direction === 'outgoing'
          ? 'Ausgehende E-Mail dokumentieren'
          : 'Eingehende E-Mail dokumentieren'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {prefill?.infoMessage && (
            <Alert severity='success' sx={{ whiteSpace: 'pre-wrap' }}>
              {prefill.infoMessage}
            </Alert>
          )}
          <Box>
            <Typography variant='caption' color='text.secondary'>
              Richtung
            </Typography>
            <RadioGroup
              row
              value={direction}
              onChange={(_, v) =>
                setDirection(v === 'outgoing' ? 'outgoing' : 'incoming')
              }
            >
              <FormControlLabel
                value='incoming'
                control={<Radio size='small' />}
                label='Eingehend (vom Mandanten erhalten)'
              />
              <FormControlLabel
                value='outgoing'
                control={<Radio size='small' />}
                label='Ausgehend (an den Mandanten gesendet)'
              />
            </RadioGroup>
          </Box>
          <TextField
            label='Von (Absender E-Mail)'
            required
            value={from}
            onChange={e => setFrom(e.target.value)}
          />
          <TextField
            label='An'
            value={to}
            onChange={e => setTo(e.target.value)}
          />
          <TextField
            label='Cc'
            value={cc}
            onChange={e => setCc(e.target.value)}
          />
          <TextField
            label='Betreff'
            required
            value={subject}
            onChange={e => setSubject(e.target.value)}
          />
          <TextField
            label='Empfangen am'
            type='datetime-local'
            value={receivedAt}
            onChange={e => setReceivedAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label='Format'
            value={bodyType}
            onChange={e => setBodyType(e.target.value as 'text' | 'html')}
          >
            <MenuItem value='text'>Klartext</MenuItem>
            <MenuItem value='html'>HTML</MenuItem>
          </TextField>
          <TextField
            label='Inhalt'
            multiline
            rows={6}
            value={body}
            onChange={e => setBody(e.target.value)}
          />
          <TextField
            label='Notiz (intern)'
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <Box>
            <Button variant='outlined' component='label' size='small'>
              Anhänge auswählen
              <input
                hidden
                type='file'
                multiple
                onChange={e => handleFiles(e.target.files)}
              />
            </Button>
            {attachments.length > 0 && (
              <Box sx={{ mt: 1, fontSize: 13, color: 'text.secondary' }}>
                {attachments.map((a, i) => (
                  <div key={i}>📎 {a.fileName}</div>
                ))}
              </Box>
            )}
          </Box>
          {error && <Alert severity='error'>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbruch</Button>
        <Button
          variant='contained'
          onClick={submit}
          disabled={isRecordingIncoming}
        >
          {isRecordingIncoming ? <CircularProgress size={18} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
